
  import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
  import { createNamespacedHelpers } from 'vuex'
  import TourStep from './TourStep.vue'
  import { Tour as ITour, TourStep as ITourStep, TourOptions } from '../models/interfaces'
  import { ActionTypes } from '../store/actions'

  const { mapState, mapGetters, mapActions } = createNamespacedHelpers('tour')

@Component({
  components: {
    TourStep,
  },
  computed: {
    ...mapState(['activeTour', 'currentStepIndex', 'isActive', 'isTourEnabled']),
    ...mapGetters(['currentStep', 'totalSteps', 'isFirstStep', 'isLastStep', 'tourProgress', 'isTourCompleted']),
  },
  methods: {
    ...mapActions({
      startTour: ActionTypes.START_TOUR,
      nextStepAction: ActionTypes.NEXT_STEP,
      prevStepAction: ActionTypes.PREV_STEP,
      endTour: ActionTypes.END_TOUR,
      skipTourAction: ActionTypes.SKIP_TOUR,
      goToStep: ActionTypes.GO_TO_STEP,
      toggleTourEnabled: ActionTypes.TOGGLE_TOUR_ENABLED,
    }),
  },
})
  export default class Tour extends Vue {
  @Prop({ type: Object, default: () => ({}) }) tour!: ITour;
  @Prop({ type: Object, default: () => ({}) }) options!: TourOptions;
  @Prop({ type: Boolean, default: false }) startOnMount!: boolean;
  @Prop({ type: Boolean, default: true }) enabled!: boolean;
  @Prop({ type: Number, default: 0.5 }) backdropOpacity!: number;

  // Propiedades mapeadas de Vuex
  readonly activeTour!: ITour | null;
  readonly currentStepIndex!: number;
  readonly isActive!: boolean;
  readonly isTourEnabled!: boolean;
  readonly currentStep!: ITourStep | null;
  readonly totalSteps!: number;
  readonly isFirstStep!: boolean;
  readonly isLastStep!: boolean;
  readonly tourProgress!: number;
  readonly isTourCompleted!: (tourId: string) => boolean;

  // Métodos mapeados de Vuex
  readonly startTour!: (tour: ITour) => Promise<void>;
  readonly nextStepAction!: () => Promise<void>;
  readonly prevStepAction!: () => Promise<void>;
  readonly endTour!: () => void;
  readonly skipTourAction!: () => void;
  readonly goToStep!: (stepIndex: number) => Promise<void>;
  readonly toggleTourEnabled!: (enabled: boolean) => void;

  // Opciones combinadas del tour
  get tourOptions (): TourOptions {
    return {
      showButtons: true,
      showProgress: true,
      showCloseButton: true,
      darkMode: false,
      backdropOpacity: this.backdropOpacity,
      ...this.options,
    }
  }

  @Watch('enabled')
  onEnabledChange (enabled: boolean) {
    this.toggleTourEnabled(enabled)
  }

  @Watch('tour')
  onTourChange (newTour: ITour) {
    if (newTour && newTour.id) {
      this.$nextTick(() => {
        this.startTour(newTour)
      })
    }
  }

  mounted () {
    this.toggleTourEnabled(this.enabled)

    if (this.startOnMount && this.tour && this.tour.id) {
      this.$nextTick(() => {
        this.startTour(this.tour)
      })
    }
  }

  /**
   * Inicia el tour actual
   */
  start () {
    if (this.tour && this.tour.id) {
      this.startTour(this.tour)
    }
  }

  /**
   * Avanza al siguiente paso
   */
  async nextStep () {
    await this.nextStepAction()
  }

  /**
   * Retrocede al paso anterior
   */
  async prevStep () {
    await this.prevStepAction()
  }

  /**
   * Omite el tour actual
   */
  skipTour () {
    this.skipTourAction()
  }

  /**
   * Finaliza el tour actual (completado)
   */
  finish () {
    this.endTour()
  }

  /**
   * Navega a un paso específico
   */
  async navigateToStep (stepIndex: number) {
    await this.goToStep(stepIndex)
  }

  /**
   * Verifica si un tour ha sido completado
   */
  isTourDone (tourId: string): boolean {
    return this.isTourCompleted(tourId)
  }

  /**
   * Reinicia el tour actual forzando una nueva inicialización
   */
  restart () {
    if (this.tour && this.tour.id) {
      // Primero finalizar el tour actual si está activo
      if (this.isActive) {
        this.endTour()
      }

      // Esperar un tick y luego reiniciar
      this.$nextTick(() => {
        this.startTour(this.tour)
      })
    }
  }

  /**
   * Finaliza y limpia completamente el tour actual
   */
  end () {
    this.endTour()
  }
  }
